import React, { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAppStore } from "../../store/app";
import { useT } from "../../lib/i18n/useT";
import { readAllVacancies, saveNewVacancy, appendVacancyRoute, type Vacancy } from "../../lib/api/vacancies";
import { readUsers } from "../../lib/fs/config";
import { sendNewVacancyEmail } from "../../lib/notifications/email";
import { type ExtractedVacancy } from "../../lib/ai/vacancy-extraction";

interface VacancyModalProps {
  extracted: ExtractedVacancy;
  screenshot?: string;
  url: string;
  onClose: () => void;
}

export default function VacancyModal({ extracted, screenshot, url, onClose }: VacancyModalProps) {
  const t = useT();
  const repoPath = useAppStore((s) => s.repoPath);
  const user = useAppStore((s) => s.user);
  const lensUser = useAppStore((s) => s.lensUser);
  const accessToken = useAppStore((s) => s.accessToken);
  const settings = useAppStore((s) => s.settings);
  const [, navigate] = useLocation();

  // Form inputs initialized from AI extracted metadata
  const [title, setTitle] = useState(extracted.title || "");
  const [client, setClient] = useState(extracted.client || "");
  const [portal, setPortal] = useState(extracted.portal || "");
  const [rate, setRate] = useState<number | "">(extracted.rate || "");
  const [hours, setHours] = useState<number | "">(extracted.hours_per_week || "");
  const [location, setLocation] = useState(extracted.location || "");
  const [remotePolicy, setRemotePolicy] = useState<"remote" | "hybrid" | "onsite">(
    extracted.remote_policy || "hybrid"
  );
  const [startDate, setStartDate] = useState(extracted.start_date || "");
  const [duration, setDuration] = useState<number | "">(extracted.duration_months || "");
  const [zzpAllowed, setZzpAllowed] = useState(extracted.zzp_allowed ?? true);
  const [languageReq, setLanguageReq] = useState(extracted.dutch_language_requirement || "B2");
  
  // Format deadline for datetime-local input
  const initialDeadlineLocal = extracted.deadline
    ? new Date(extracted.deadline).toISOString().substring(0, 16)
    : "";
  const [deadline, setDeadline] = useState(initialDeadlineLocal);

  // Requirements lists
  const [hardReqs, setHardReqs] = useState<string[]>(extracted.requirements?.hard || []);
  const [preferredReqs, setPreferredReqs] = useState<string[]>(extracted.requirements?.preferred || []);
  const [competencies, setCompetencies] = useState<string[]>(extracted.requirements?.competencies || []);

  const [newHard, setNewHard] = useState("");
  const [newPref, setNewPref] = useState("");
  const [newComp, setNewComp] = useState("");

  const [existingVacancies, setExistingVacancies] = useState<Vacancy[]>([]);
  const [duplicateVacancy, setDuplicateVacancy] = useState<Vacancy | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"general" | "details" | "requirements">("general");
  const [fullText, setFullText] = useState(extracted.full_text || "");
  const [detailsSubTab, setDetailsSubTab] = useState<"summary" | "full_text">("summary");
  const [activeReqSubTab, setActiveReqSubTab] = useState<"hard" | "preferred" | "competencies">("hard");

  // Load existing vacancies to run duplicate check
  useEffect(() => {
    if (!repoPath) return;
    readAllVacancies(repoPath).then((all) => {
      setExistingVacancies(all);
    }).catch(console.error);
  }, [repoPath]);

  // Real-time duplicate check
  useEffect(() => {
    if (!title || !client || existingVacancies.length === 0) {
      setDuplicateVacancy(null);
      return;
    }
    const dup = existingVacancies.find(
      (v) =>
        v.title.toLowerCase().trim() === title.toLowerCase().trim() &&
        v.client.toLowerCase().trim() === client.toLowerCase().trim()
    );
    setDuplicateVacancy(dup || null);
  }, [title, client, existingVacancies]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!repoPath || !user?.oid) return;

    if (!title || !client || !portal) {
      setErrorMsg("Please fill in all mandatory fields (Title, Client, Portal).");
      return;
    }

    setSaving(true);
    setErrorMsg(null);

    try {
      const utcDeadline = deadline ? new Date(deadline).toISOString() : new Date().toISOString();
      const currentIsoTime = new Date().toISOString();

      const routeData = {
        portal,
        url,
        rate: Number(rate) || 0,
        deadline: utcDeadline,
        captured_at: currentIsoTime,
        captured_by_oid: user.oid,
      };

      let savedVacancy: Vacancy;

      if (duplicateVacancy) {
        // Append route to existing vacancy instead of duplicates (spec §9)
        savedVacancy = await appendVacancyRoute(repoPath, duplicateVacancy.id, routeData, screenshot ?? "");
      } else {
        // Save as fresh vacancy
        const vacancyData = {
          title,
          client,
          added_at: currentIsoTime,
          added_by_oid: user.oid,
          hours_per_week: Number(hours) || 36,
          location,
          remote_policy: remotePolicy,
          start_date: startDate || "Immediately",
          duration_months: Number(duration) || 6,
          zzp_allowed: zzpAllowed,
          dutch_language_requirement: languageReq,
          requirements: {
            hard: hardReqs,
            preferred: preferredReqs,
            competencies,
          },
          full_text: fullText,
        };

        savedVacancy = await saveNewVacancy(repoPath, vacancyData, routeData, screenshot ?? "");
      }

      // Graph API Mail Notification (spec §8.1)
      if (settings?.notification_email_enabled && accessToken) {
        try {
          const registryUsers = await readUsers(repoPath);
          const recipientEmails = registryUsers.map((u) => u.email).filter(Boolean);
          const creatorName = lensUser?.display_name ?? user.displayName;

          await sendNewVacancyEmail(accessToken, savedVacancy, creatorName, recipientEmails);
        } catch (mailErr) {
          console.error("Email notification failed to send:", mailErr);
          // Don't fail the whole save operation if only email notification fails
        }
      }

      setSaving(false);
      onClose();
      navigate("/vacancies");
    } catch (err: any) {
      console.error(err);
      const errMsg = err?.message || String(err);
      setErrorMsg(`Failed to write vacancy to disk: ${errMsg}`);
      setSaving(false);
    }
  }

  // Tags helper functions
  const addTag = (list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>, val: string, setVal: React.Dispatch<React.SetStateAction<string>>) => {
    if (!val.trim()) return;
    setList([...list, val.trim()]);
    setVal("");
  };

  const removeTag = (list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>, index: number) => {
    setList(list.filter((_, idx) => idx !== index));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs overflow-y-auto p-4">
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 md:p-8 animate-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
          <h2 className="text-xl font-bold text-slate-800 tracking-tight">
            Review Vacancy Details
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-lg font-bold focus:outline-none cursor-pointer"
          >
            ✕
          </button>
        </div>

        {/* Duplicate Warning Banner */}
        {duplicateVacancy && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex gap-2">
              <span className="text-base">⚠️</span>
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  {t("vacancy.duplicate", { portal })}
                </p>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  Saving will append this portal to the existing vacancy card (<strong>{duplicateVacancy.client} — {duplicateVacancy.title}</strong>) as a new procurement route.
                </p>
              </div>
            </div>
          </div>
        )}

        {errorMsg && (
          <div className="mb-6 rounded-lg bg-red-50 p-3.5 border border-red-100">
            <p className="text-xs font-semibold text-red-600 text-center leading-relaxed">{errorMsg}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSave} className="space-y-6">
          
          {/* Tabs Navigation */}
          <div className="flex border-b border-slate-200 mb-6 gap-1.5">
            {(["general", "details", "requirements"] as const).map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2.5 -mb-px border text-xs font-bold uppercase tracking-wider rounded-t-xl transition-all duration-150 cursor-pointer focus:outline-none ${
                    isActive
                      ? "bg-white border-slate-200 border-b-white text-slate-900 shadow-2xs"
                      : "bg-slate-50/55 border-slate-100 border-b-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-650"
                  }`}
                >
                  {t(`vacancy_modal.tab.${tab}` as any)}
                </button>
              );
            })}
          </div>

          {/* General Tab */}
          {activeTab === "general" && (
            <div className="space-y-6 animate-in fade-in duration-150">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Title */}
                <div className="form-group">
                  <label className="form-label">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    className="form-input"
                  />
                </div>

                {/* Client */}
                <div className="form-group">
                  <label className="form-label">
                    Client Organization *
                  </label>
                  <input
                    type="text"
                    value={client}
                    onChange={(e) => setClient(e.target.value)}
                    required
                    className="form-input"
                  />
                </div>

                {/* Portal */}
                <div className="form-group">
                  <label className="form-label">
                    Portal Source *
                  </label>
                  <input
                    type="text"
                    value={portal}
                    onChange={(e) => setPortal(e.target.value)}
                    placeholder="e.g. Striive, Between, HeadFirst"
                    required
                    className="form-input"
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Location */}
                <div className="form-group">
                  <label className="form-label">
                    Location
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="form-input"
                  />
                </div>

                {/* Remote Policy */}
                <div className="form-group">
                  <label className="form-label">
                    Remote Policy
                  </label>
                  <select
                    value={remotePolicy}
                    onChange={(e) => setRemotePolicy(e.target.value as any)}
                    className="form-select"
                  >
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">On-Site</option>
                  </select>
                </div>

              </div>

              {/* ZZP Allowed toggle */}
              <div className="flex items-center gap-2 bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                <input
                  id="zzp"
                  type="checkbox"
                  checked={zzpAllowed}
                  onChange={(e) => setZzpAllowed(e.target.checked)}
                  className="h-4 w-4 rounded-sm border-slate-300 text-slate-900 focus:ring-slate-900"
                />
                <label htmlFor="zzp" className="text-xs font-semibold text-slate-700 select-none cursor-pointer">
                  Freelance / ZZP contracts are allowed for this vacancy
                </label>
              </div>
            </div>
          )}

          {/* Procurement & Details Tab */}
          {activeTab === "details" && (
            <div className="space-y-6 animate-in fade-in duration-150">
              
              {/* Details Sub-Tabs (Segmented Control style) */}
              <div className="bg-slate-100/70 p-1 rounded-xl flex gap-1 w-full max-w-2xl border border-slate-200/40 mb-6">
                {(["summary", "full_text"] as const).map((sub) => (
                  <button
                    key={sub}
                    type="button"
                    onClick={() => setDetailsSubTab(sub)}
                    className={`flex-1 py-2 rounded-lg text-[10px] md:text-xs font-semibold tracking-wider transition-all cursor-pointer focus:outline-none uppercase ${
                      detailsSubTab === sub
                        ? "bg-white text-slate-950 shadow-xs border border-slate-200/35"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {t(`vacancy_modal.subtab.${sub}` as any)}
                  </button>
                ))}
              </div>

              {/* Summary Sub-Panel */}
              {detailsSubTab === "summary" && (
                <div className="space-y-6 animate-in fade-in duration-100">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Hourly Rate */}
                    <div className="form-group">
                      <label className="form-label">Hourly Rate (€/h)</label>
                      <input
                        type="number"
                        value={rate}
                        onChange={(e) => setRate(e.target.value === "" ? "" : Number(e.target.value))}
                        className="form-input"
                      />
                    </div>
                    {/* Hours per Week */}
                    <div className="form-group">
                      <label className="form-label">Hours per Week</label>
                      <input
                        type="number"
                        value={hours}
                        onChange={(e) => setHours(e.target.value === "" ? "" : Number(e.target.value))}
                        className="form-input"
                      />
                    </div>
                    {/* Dutch Level */}
                    <div className="form-group">
                      <label className="form-label">Dutch language level</label>
                      <select
                        value={languageReq}
                        onChange={(e) => setLanguageReq(e.target.value)}
                        className="form-select"
                      >
                        <option value="None">None</option>
                        <option value="A1">A1</option>
                        <option value="A2">A2</option>
                        <option value="B1">B1</option>
                        <option value="B2">B2</option>
                        <option value="C1">C1</option>
                        <option value="C2">C2</option>
                        <option value="Native">Native</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Start Date */}
                    <div className="form-group">
                      <label className="form-label">Start Date</label>
                      <input
                        type="text"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        placeholder="e.g. 2026-07-01 or ASAP"
                        className="form-input"
                      />
                    </div>
                    {/* Duration (Months) */}
                    <div className="form-group">
                      <label className="form-label">Duration (Months)</label>
                      <input
                        type="number"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value === "" ? "" : Number(e.target.value))}
                        className="form-input"
                      />
                    </div>
                    {/* Deadline */}
                    <div className="form-group">
                      <label className="form-label">Route Deadline</label>
                      <input
                        type="datetime-local"
                        value={deadline}
                        onChange={(e) => setDeadline(e.target.value)}
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Full Text Sub-Panel */}
              {detailsSubTab === "full_text" && (
                <div className="form-group animate-in fade-in duration-100">
                  <label className="form-label">
                    {t("vacancy_modal.label.full_text")}
                  </label>
                  <textarea
                    value={fullText}
                    onChange={(e) => setFullText(e.target.value)}
                    rows={12}
                    className="w-full text-xs rounded-lg border border-slate-200 bg-white p-3.5 text-slate-700 shadow-2xs focus:border-indigo-500 focus:outline-none transition-colors"
                    placeholder="Type or paste the complete vacancy description text here..."
                  />
                </div>
              )}

            </div>
          )}

          {/* Requirements Tab */}
          {activeTab === "requirements" && (
            <div className="space-y-6 animate-in fade-in duration-150">
              
              {/* Requirements Sub-Tabs (Segmented Control style) */}
              <div className="bg-slate-100/70 p-1 rounded-xl flex gap-1 w-full max-w-2xl border border-slate-200/40 mb-6">
                {(["hard", "preferred", "competencies"] as const).map((sub) => (
                  <button
                    key={sub}
                    type="button"
                    onClick={() => setActiveReqSubTab(sub)}
                    className={`flex-1 py-2 rounded-lg text-[10px] md:text-xs font-semibold tracking-wider transition-all cursor-pointer focus:outline-none uppercase ${
                      activeReqSubTab === sub
                        ? "bg-white text-slate-955 shadow-xs border border-slate-200/35"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {t(`detail_modal.req.${sub}` as any)}
                  </button>
                ))}
              </div>

              {/* Hard Requirements Sub-Panel */}
              {activeReqSubTab === "hard" && (
                <div className="flex flex-col gap-3.5 animate-in fade-in duration-100">
                  <div className="flex flex-wrap gap-1.5">
                    {hardReqs.length === 0 ? (
                      <span className="text-xs text-slate-400 italic">No hard requirements added yet.</span>
                    ) : (
                      hardReqs.map((req, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 rounded-md bg-rose-55 border border-rose-100/50 px-2 py-0.5 text-xs font-medium text-rose-700"
                        >
                          {req}
                          <button
                            type="button"
                            onClick={() => removeTag(hardReqs, setHardReqs, idx)}
                            className="text-rose-400 hover:text-rose-600 focus:outline-none cursor-pointer"
                          >
                            ✕
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <div className="mt-1 flex max-w-md gap-2">
                    <input
                      type="text"
                      value={newHard}
                      onChange={(e) => setNewHard(e.target.value)}
                      placeholder="Add skill, experience requirement…"
                      className="form-input flex-1 py-1.5 text-xs"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTag(hardReqs, setHardReqs, newHard, setNewHard);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => addTag(hardReqs, setHardReqs, newHard, setNewHard)}
                      className="rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 cursor-pointer focus:outline-none"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Preferred Requirements Sub-Panel */}
              {activeReqSubTab === "preferred" && (
                <div className="flex flex-col gap-3.5 animate-in fade-in duration-100">
                  <div className="flex flex-wrap gap-1.5">
                    {preferredReqs.length === 0 ? (
                      <span className="text-xs text-slate-400 italic">No preferred requirements added yet.</span>
                    ) : (
                      preferredReqs.map((req, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-100/50 px-2 py-0.5 text-xs font-medium text-amber-700"
                        >
                          {req}
                          <button
                            type="button"
                            onClick={() => removeTag(preferredReqs, setPreferredReqs, idx)}
                            className="text-amber-400 hover:text-amber-600 focus:outline-none cursor-pointer"
                          >
                            ✕
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <div className="mt-1 flex max-w-md gap-2">
                    <input
                      type="text"
                      value={newPref}
                      onChange={(e) => setNewPref(e.target.value)}
                      placeholder="Add nice-to-have skill…"
                      className="form-input flex-1 py-1.5 text-xs"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTag(preferredReqs, setPreferredReqs, newPref, setNewPref);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => addTag(preferredReqs, setPreferredReqs, newPref, setNewPref)}
                      className="rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 cursor-pointer focus:outline-none"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Competencies Sub-Panel */}
              {activeReqSubTab === "competencies" && (
                <div className="flex flex-col gap-3.5 animate-in fade-in duration-100">
                  <div className="flex flex-wrap gap-1.5">
                    {competencies.length === 0 ? (
                      <span className="text-xs text-slate-400 italic">No competencies added yet.</span>
                    ) : (
                      competencies.map((req, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 rounded-md bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                        >
                          {req}
                          <button
                            type="button"
                            onClick={() => removeTag(competencies, setCompetencies, idx)}
                            className="text-indigo-400 hover:text-indigo-600 focus:outline-none cursor-pointer"
                          >
                            ✕
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <div className="mt-1 flex max-w-md gap-2">
                    <input
                      type="text"
                      value={newComp}
                      onChange={(e) => setNewComp(e.target.value)}
                      placeholder="Add competency…"
                      className="form-input flex-1 py-1.5 text-xs"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTag(competencies, setCompetencies, newComp, setNewComp);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => addTag(competencies, setCompetencies, newComp, setNewComp)}
                      className="rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 cursor-pointer focus:outline-none"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

            </div>
          )}



          {/* Action Buttons */}
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-6 mt-8">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200/50 px-4.5 py-2 text-xs font-semibold text-slate-600 transition-colors focus:outline-none cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4.5 py-2 text-xs font-semibold shadow-xs hover:shadow-sm transition-all cursor-pointer focus:outline-none disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-wait"
            >
              {saving ? (
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-white" />
                  Saving…
                </span>
              ) : (
                "Save Vacancy"
              )}
            </button>
          </div>

        </form>

      </div>
    </div>
  );
}
